import type { ILineItem, IReturnLineItem } from "../src/api/fetchOrder";
import { fetchOrder } from "../src/api/fetchOrder";
import { useEffect, useState } from "react";
import { extractId } from "../src/utils";
import type { ICustomerQuery, IOrdersEdgesNode } from "../src/queries/customer-gql";
import { FinancialStatus } from "../src/utils/order";
import uuid from "react-uuid";

const STORE_NAME = import.meta.env.VITE_REACT_APP_NAME as string;

export default function useOrderDetail({
  lang,
  order,
  customer,
  handle,
}: {
  lang: "ko" | "en" | "jp";
  order: IOrdersEdgesNode | null;
  customer: ICustomerQuery["customer"];
  handle?: string;
}) {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /* 주문 진행 내역을 관리하는 state - (unfulfilled, fulfilled, onhold) */
  const [completeOrder, setCompleteOrder] = useState<ILineItem[]>([]);
  /* 반품 내역을 관리하는 state - (returnRequested, returnApproved) */
  const [returnOrder, setReturnOrder] = useState<IReturnLineItem[]>([]);
  /* 환불 내역을 관리하는 state - (canceled, refunded) */
  const [refundOrder, setRefundOrder] = useState<ILineItem[]>([]);
  /** 반품 배송비를 관리하는 state */
  const [totalReturnShippingFee, setTotalReturnShippingFee] = useState<number | null>(null);

  useEffect(() => {
    if (!order) return;

    /**
     * 부분취소 혹은 반품접수한 이력이 있는 경우
     * returnStatus(메타필드)의 값이 존재하는 경우, 반품 이력이 존재한다는 뜻
     * CS팀에서 부분 `취소`를 한 경우, 반품을 하지 않아도 PartiallyRefunded 상태이므로 어드민 API를 호출
     */
    const isReturnOrPartiallyRefunded =
      Boolean(order.returnStatus?.value) ||
      order.financialStatus === FinancialStatus.PartiallyRefunded ||
      order.financialStatus === FinancialStatus.PartiallyPaid;

    // 아카이브된 상품이거나 삭제된 상품의 경우 어드민 API를 호출
    const hasDeletedProduct = order.lineItems.edges.some((lineItem) => lineItem.node.variant === null);

    if (isReturnOrPartiallyRefunded || hasDeletedProduct) {
      void getOrder();
    } else {
      const orderLineItems = order.lineItems.edges.map(({ node }) => {
        return {
          ...node,
          id: uuid(),
          image: node.variant?.image,
          variantTitle: node.variant?.title, // variant가 null인 경우 size 표기를 위해 variantTitle를 가져옴
          variant: node.variant,
          originalTotalPrice: {
            ...node.originalTotalPrice,
            amount: (parseFloat(node.originalTotalPrice.amount) / node.quantity).toString(),
          },
          discountedTotalPrice: {
            ...node.discountedTotalPrice,
            amount: (parseFloat(node.discountedTotalPrice.amount) / node.quantity).toString(),
          },
          originalQuantity: node.quantity,
        };
      });

      if (order.financialStatus === FinancialStatus.Refunded) {
        // 취소 or 전체 환불을 어드민에서 처리해줄 경우
        setRefundOrder(orderLineItems as ILineItem[]);
      } else {
        setCompleteOrder(orderLineItems as ILineItem[]);
      }
    }
  }, [handle]);

  const getOrder = async () => {
    setIsLoading(true);

    try {
      const response = await fetchOrder({
        storeName: STORE_NAME,
        domain: lang === "ko" ? "" : "intl",
        orderId: extractId(order?.id || ""),
        customerId: customer.id,
      });

      if (!response) {
        window.history.go(-1);
        return;
      }

      setTotalReturnShippingFee(response.totalReturnShippingFee);
      setCompleteOrder(response.lineItems);
      setReturnOrder(response.returnLineItems);
      setRefundOrder(response.refundLineItems);
    } catch (error) {
      window.history.go(-1);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    completeOrder,
    returnOrder,
    refundOrder,
    totalReturnShippingFee,
    isLoading,
  };
}
